// STICKERS (owner 2026-10-14: « add all the assets png images that u generated
// in the editor so people can add them as stickers — it need to be fast and
// smooth in this place »).
//
// The design system v2 decoration pieces (public bucket `decor`) that make
// sense on their own — objects, clips, tapes, labels and the voyage stickers;
// never paper, frames, masks, letters or colour maps. Three files per sticker,
// none of them a Supabase image transform (billed per origin image):
//   · thumbs/<path>.webp  256 px, ≤ 20 KB — the panel's tiles;
//   · med/<path>.webp     900 px          — the editor canvas (PageElement.previewUrl);
//   · <path>.png          the original    — the PRINT (PageElement.imageUrl).
// catalog.json is generated by scripts/themes/build_sticker_catalog.py from
// the local pieces (w/h = the original's pixels: a tap places the sticker at
// its true aspect without loading anything). It is loaded on demand
// (loadStickerData) so the editor bundle does not carry it.
//
// Pure (no React). Guard: catalog.test.ts

import { PAGE_W_MM, PAGE_H_MM } from '../captionLayout';

export type StickerType = 'object' | 'sticker' | 'label' | 'clip' | 'tape';

export interface Sticker {
  /** `<theme>/<type>/<name>` — also the path of its thumb and med files. */
  id: string;
  theme: string;
  type: StickerType;
  /** French name (tile title). */
  name: string;
  /** Search words, accent-free, space-separated (French + English). */
  kw: string;
  /** Original PNG in the `decor` bucket. */
  path: string;
  /** The ORIGINAL's pixels. */
  w: number;
  h: number;
  /** The design system's one soft shadow (objects, labels; never ink, tape or clips). */
  shadow: boolean;
}

/** The ten themes, in the panel's order. */
export const STICKER_THEMES: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'voyage', label: 'Voyage' },
  { slug: 'mariage', label: 'Mariage' },
  { slug: 'maroc', label: 'Maroc' },
  { slug: 'famille', label: 'Famille' },
  { slug: 'memories', label: 'Souvenirs' },
  { slug: 'anniversaire', label: 'Anniversaire' },
  { slug: 'religieux', label: 'Religieux' },
  { slug: 'etudes', label: 'Études' },
  { slug: 'naissance', label: 'Naissance' },
  { slug: 'autre', label: 'Autre' },
];

export const STICKER_TYPES: ReadonlyArray<{ type: StickerType; label: string }> = [
  { type: 'object', label: 'Objets' },
  { type: 'sticker', label: 'Stickers' },
  { type: 'label', label: 'Étiquettes' },
  { type: 'clip', label: 'Pinces' },
  { type: 'tape', label: 'Rubans' },
];

/** What the panel never offers (backgrounds, frames, masks, letters, colour maps, the Storiz mark). */
export const EXCLUDED_TYPES = ['paper', 'frame', 'mask', 'letter', 'letter-tint', 'tint'] as const;

/** Which theme an album is, from its cover's categories: the most specific one
 *  first — the same order as the worker (themes/themeForAlbum.ts THEME_PRIORITY). */
export const THEME_PRIORITY = ['religieux', 'mariage', 'naissance', 'anniversaire', 'etudes', 'famille', 'maroc', 'memories', 'voyage', 'autre'] as const;

export function albumThemeFrom(pageTheme: string | null | undefined, coverCategories: readonly string[] | null | undefined): string | null {
  if (pageTheme && STICKER_THEMES.some((t) => t.slug === pageTheme)) return pageTheme;
  const cats = new Set((coverCategories ?? []).map((c) => String(c).toLowerCase().trim()));
  return THEME_PRIORITY.find((t) => cats.has(t)) ?? null;
}

const SUPABASE_URL = (
  (import.meta.env?.VITE_SUPABASE_URL as string | undefined) || 'https://arvlsxlmfgqyvwdpxjwn.supabase.co'
).replace(/\/$/, '');
export const DECOR_BASE = `${SUPABASE_URL}/storage/v1/object/public/decor/`;

/** The print file — what the PDF embeds. */
export const stickerOriginalUrl = (s: Pick<Sticker, 'path'>) => `${DECOR_BASE}${s.path}`;
/** The panel tile (256 px). */
export const stickerThumbUrl = (s: Pick<Sticker, 'id'>) => `${DECOR_BASE}thumbs/${s.id}.webp`;
/** The editor canvas (900 px). */
export const stickerMedUrl = (s: Pick<Sticker, 'id'>) => `${DECOR_BASE}med/${s.id}.webp`;

/** Accent-free, lower-case — the catalogue's `kw` is written the same way. */
export const fold = (s: string) => s.toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export interface StickerFilter {
  /** Theme slug, or 'all'. A search looks in every theme. */
  theme: string;
  /** Type, or 'all'. */
  type: StickerType | 'all';
  query?: string;
}

/** Every word of the query must start a word of the sticker's name/keywords. */
export function filterStickers(all: readonly Sticker[], f: StickerFilter): Sticker[] {
  const words = fold(f.query ?? '').split(/[^a-z0-9]+/).filter(Boolean);
  return all.filter((s) => {
    if (f.type !== 'all' && s.type !== f.type) return false;
    if (!words.length) return f.theme === 'all' || s.theme === f.theme;
    const hay = ` ${s.kw} ${fold(s.name).replace(/[^a-z0-9]+/g, ' ')}`;
    return words.every((w) => hay.includes(` ${w}`));
  });
}

/** The types present in a list (chips only show what has content). */
export function typesIn(list: readonly Sticker[]): StickerType[] {
  const present = new Set(list.map((s) => s.type));
  return STICKER_TYPES.map((t) => t.type).filter((t) => present.has(t));
}

/** The sticker's width at its first placement: a quarter of the page width… */
export const STICKER_DEFAULT_W = 0.25;
/** …unless that makes it taller than a quarter of the page height. */
export const STICKER_MAX_H = 0.25;

export interface StickerElement {
  id: string;
  type: 'icon';
  x: number; y: number; width: number; height: number;
  /** The original — the PDF prints it. */
  imageUrl: string;
  /** The editor's lighter copy (never printed). */
  previewUrl: string;
  shadow?: boolean;
}

/**
 * The page element a tap adds: centred on the page at a quarter of its width,
 * the box at the ORIGINAL's aspect in millimetres (x/width in % of the page
 * width, y/height in % of its height — the PDF's trim, 200 × 297 mm). A sticker
 * never lands exactly on another element's centre (two taps would hide the
 * first): each one there pushes it 4 % down-right, staying on the page.
 */
export function stickerElement(
  s: Pick<Sticker, 'id' | 'path' | 'w' | 'h' | 'shadow'>,
  existing: ReadonlyArray<{ x: number; y: number; width: number; height: number }> = [],
  id: string = `el-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
): StickerElement {
  const aspect = s.w > 0 && s.h > 0 ? s.w / s.h : 1;
  let wMm = PAGE_W_MM * STICKER_DEFAULT_W;
  let hMm = wMm / aspect;
  if (hMm > PAGE_H_MM * STICKER_MAX_H) { hMm = PAGE_H_MM * STICKER_MAX_H; wMm = hMm * aspect; }
  const width = (wMm / PAGE_W_MM) * 100;
  const height = (hMm / PAGE_H_MM) * 100;
  let x = (100 - width) / 2;
  let y = (100 - height) / 2;
  const taken = (cx: number, cy: number) => existing.some((e) => Math.abs(e.x + e.width / 2 - cx) < 2 && Math.abs(e.y + e.height / 2 - cy) < 2);
  for (let i = 0; i < 12 && taken(x + width / 2, y + height / 2); i++) {
    x = Math.min(100 - width, x + 4);
    y = Math.min(100 - height, y + 4);
  }
  return {
    id, type: 'icon', x, y, width, height,
    imageUrl: stickerOriginalUrl(s),
    previewUrl: stickerMedUrl(s),
    ...(s.shadow ? { shadow: true } : {}),
  };
}

// ── Lazy data ────────────────────────────────────────────────────────────────

export interface StickerData {
  stickers: Sticker[];
  /** « Icônes » tiles: path in the covers bucket → tile colour ('' = the cream tile). */
  coverThumbs: Record<string, string>;
}

let loading: Promise<StickerData> | null = null;
let loaded: StickerData | null = null;

/** The catalogue and the cover-thumb list, fetched once (their own chunks). */
export function loadStickerData(): Promise<StickerData> {
  if (loaded) return Promise.resolve(loaded);
  if (!loading) {
    loading = Promise.all([import('./catalog.json'), import('./coverIconThumbs.json')])
      .then(([c, t]) => {
        loaded = {
          stickers: (c.default as unknown as Sticker[]),
          coverThumbs: (t.default as unknown as Record<string, string>),
        };
        return loaded;
      })
      .catch((e) => { loading = null; throw e; });
  }
  return loading;
}

/** Synchronous read once loaded (null before). */
export const stickerDataNow = (): StickerData | null => loaded;
